/**
 * Live preview of rule evaluation from form draft (before Apply persists).
 */
import {
  type EvaluatedElement,
  evaluateRule,
  type RuleResult,
  type RuleSpec,
} from "@vera/rules";
import type { Violation } from "@vera/shared";
import {
  mergeElementWithUserFacts,
  type UserFactValue,
} from "../stores/userFactStore";
import type { MissingFactField } from "./missingFacts";
import {
  evaluatePredicateChecks,
  type RuleVerdict,
  ruleVerdictFromChecks,
} from "./rulePredicateChecks";

export function draftOverridesFromForm(
  fields: MissingFactField[],
  draft: Record<string, string>,
  parse: (field: MissingFactField, raw: string) => UserFactValue | undefined
): Record<string, UserFactValue> {
  const out: Record<string, UserFactValue> = {};
  for (const field of fields) {
    const value = parse(field, draft[field.factKey] ?? "");
    if (value !== undefined) out[field.factKey] = value;
  }
  return out;
}

export function mergedFactsForPreview(
  element: EvaluatedElement,
  savedOverrides: Record<string, UserFactValue> | undefined,
  draftOverrides: Record<string, UserFactValue>
): EvaluatedElement {
  const mergedSaved = { ...(savedOverrides ?? {}), ...draftOverrides };
  return mergeElementWithUserFacts(element, {
    [element.dbId]: mergedSaved,
  });
}

export function previewRuleResult(
  rule: RuleSpec,
  element: EvaluatedElement,
  savedOverrides: Record<string, UserFactValue> | undefined,
  draftOverrides: Record<string, UserFactValue>
): RuleResult | null {
  const mergedEl = mergedFactsForPreview(
    element,
    savedOverrides,
    draftOverrides
  );
  return evaluateRule(rule, mergedEl);
}

export function previewViolationFromResult(
  base: Violation,
  result: RuleResult | null,
  rule: RuleSpec
): Violation | null {
  if (!result || !base.provenance) return null;
  const severity =
    result.status === "confirmed_violation"
      ? rule.severity
      : result.status === "indeterminate"
        ? "warning"
        : "info";
  return {
    ...base,
    summary: result.summary,
    rationale: result.rationale,
    severity,
    provenance: {
      ...base.provenance,
      status: result.status,
      observed: result.observedValue,
      threshold: result.thresholdApplied,
      exceptionsConsidered: result.exceptionsConsidered,
    },
  };
}

export interface PreviewBanner {
  tone: RuleVerdict["tone"];
  title: string;
  body: string;
}

export function previewBannerForResult(
  result: RuleResult | null,
  rule: RuleSpec,
  facts: EvaluatedElement["facts"]
): PreviewBanner | null {
  if (!result) return null;
  const checks = evaluatePredicateChecks(rule, facts);
  const verdict = ruleVerdictFromChecks(checks, result);
  return {
    tone: verdict.tone,
    title: verdict.headline,
    body: verdict.detail,
  };
}

export function verdictForViolation(
  rule: RuleSpec,
  element: EvaluatedElement,
  violation: Violation,
  savedOverrides?: Record<string, UserFactValue>
): RuleVerdict {
  const merged = mergeElementWithUserFacts(element, {
    [element.dbId]: savedOverrides ?? {},
  });
  const checks = evaluatePredicateChecks(rule, merged.facts);
  const status = violation.provenance?.status ?? "indeterminate";
  return ruleVerdictFromChecks(checks, {
    ruleId: rule.ruleId,
    dbId: element.dbId,
    status,
    summary: violation.summary,
  } as RuleResult);
}
